import React, { useState } from "react";
import 'bootstrap/dist/css/bootstrap.min.css';
import '../UI/Login.css';
import { useNavigate } from "react-router-dom";
import { GetLogin } from "../Services/Authservices";
import { GetDoctorInfo, GetFontStyles, GetLogoImage } from '../Services/Template';
import { healthCommissionService, cacheHealthCommission } from '../Services/HealthCommissionService';
import SpinnerModal from "../Spinner/SpinnerModal";
import { jwtDecode } from "jwt-decode";
import { toast } from "react-toastify";
import Logo from "./Logo/Logo";

const Login = () => {
    const [formData, setFormData] = useState({ Email: "", Password: "" });
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    const onchange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });
    };

    const FormSubmut = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            const { token } = await GetLogin(formData);
            sessionStorage.setItem("token", token);

            if (token) {
                const decodedToken = jwtDecode(token);
                const labId = decodedToken.LabID;
                const userId = decodedToken.UserId;
                const User_Role = decodedToken.role;
                const FirstName = decodedToken.FirstName;
                const LastName = decodedToken.LastName;

                sessionStorage.setItem('LabID', labId);
                sessionStorage.setItem('UserID', userId);
                sessionStorage.setItem('User_Role', User_Role);
                sessionStorage.setItem("FirstName", FirstName);
                sessionStorage.setItem("LastName", LastName);

                // The platform super admin has no laboratory - skip the
                // lab-specific setup and go straight to its own page.
                if (User_Role === 'SuperAdmin') {
                    navigate('/SuperAdmin');
                    return;
                }

                if (labId) {
                    // Fetch templates & settings in parallel
                    await Promise.all([
                        DoctorInfo(labId),
                        Fontstyle(labId),
                        getLogo(labId),
                        loadHealthCommission()
                    ]);
                    // Billing due-date status is checked live by BillingWarningModal
                    // on every app shell mount, so it never relies on a flag that
                    // could go stale for the rest of the session.
                }

                if (User_Role === 'Admin') {
                    navigate('/Home/Dashboard');
                } else {
                    navigate('/Home/PatientRegistration');
                }
            } else {
                toast.error("Email and password are not correct");
            }
        } catch (error) {
            console.error("Login failed:", error);
            // Show the API's reason (wrong password, lab suspended,
            // subscription expired) when it gives one.
            toast.error(error?.response?.data?.message || "Login failed. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const DoctorInfo = async (LabID) => {
        try {
            const response = await GetDoctorInfo(LabID);
            if (response != null) {
                localStorage.setItem('DoctorInfo', JSON.stringify(response));
            }
        } catch (error) {
            toast.error(error?.message || "Failed to load doctor info");
        }
    };

    const Fontstyle = async (LabID) => {
        try {
            const response = await GetFontStyles(LabID);
            if (response != null) {
                localStorage.setItem('FontStyle', JSON.stringify(response?.[0]));
            } else {
                localStorage.setItem('FontStyle', "");
            }
        } catch (error) {
            toast.error(error?.message || "Failed to load font styles");
        }
    };

    // Health commission reg. no. + logo for the report templates. Silent on
    // failure (e.g. table not created yet) - reports just won't show it.
    const loadHealthCommission = async () => {
        try {
            cacheHealthCommission(await healthCommissionService.get());
        } catch (error) {
            cacheHealthCommission(null);
        }
    };

    const getLogo = async (LabID) => {
        try {
            const response = await GetLogoImage(LabID);
            if (response != null) {
                localStorage.setItem('LogoImage', response?.[0]?.data);
            }
        } catch (error) {
            toast.error(error?.message || "Failed to load logo");
        }
    };

    return (
        <>
            {isLoading && <SpinnerModal isLoading={true} />}
            
            <div className="login-wrapper min-vh-100 d-flex flex-column common-background-color common-FontFamily">
                {/* Navbar Header */}
                <header className="login-navbar bg-white border-bottom py-2 px-4 d-flex align-items-center justify-content-between sticky-top">
                    <div className="d-flex align-items-center gap-2">
                        <Logo width="36px" height="36px" />
                        <span className="fs-5 commont-Font-Bold common-color tracking-tight ms-1">
                            Care <span className="text-dark">Linq</span>
                        </span>
                    </div>
                    <div className="d-flex align-items-center gap-3">
                        <span className="common-FontSize-small text-muted d-none d-sm-inline">
                            Clinical Diagnostics & EMR Suite
                        </span>
                       <button 
                         onClick={() => navigate('/subscriptions')}
                        type="submit" 
                        className="Submit_Button  common-FontFamily common-FontWeight common-cursor py-2"
                    >
                        Sign Up
                    </button>
                    </div>
                </header>

                {/* Hero / Main Split Content Body */}
                <main className="flex-grow-1 d-flex align-items-center justify-content-center py-4 px-3">
                    <div className="container">
                        <div className="row g-4 align-items-center justify-content-center">
                            
                            {/* Left Side: Value Proposition & Product Details */}
                            <div className="col-lg-7 col-md-10 text-start pe-lg-5">
                                <span className="badge bg-light text-primary border mb-3 px-3 py-2 common-FontSize-small common-FontWeight common-br-3px">
                                    Next-Gen Diagnostic Software
                                </span>
                                <h1 className="display-6 commont-Font-Bold text-dark mb-3">
                                    Unified Laboratory Management, Diagnostics & Patient Billing
                                </h1>
                                <p className="text-secondary common-FontSize-small mb-4 leading-relaxed" style={{ fontSize: '14px', lineHeight: '1.6' }}>
                                    Streamline lab technician workflows, print branded clinical reports, and automate subscription billing directly from one secure, cloud-enabled portal.
                                </p>

                                {/* Key Features List */}
                                <div className="row g-3 mb-4">
                                    <div className="col-sm-6">
                                        <div className="d-flex align-items-start gap-2">
                                            <span className="common-color commont-Font-Bold fs-5">✓</span>
                                            <div>
                                                <div className="common-FontWeight common-FontSize-small text-dark">Real-Time Patient Billing</div>
                                                <div className="text-muted common-Font-10">Generate receipts, manage pending tests, and track dues.</div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-sm-6">
                                        <div className="d-flex align-items-start gap-2">
                                            <span className="common-color commont-Font-Bold fs-5">✓</span>
                                            <div>
                                                <div className="common-FontWeight common-FontSize-small text-dark">Custom Report Templates</div>
                                                <div className="text-muted common-Font-10">Tailor header styles, doctor info, and logos dynamically.</div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-sm-6">
                                        <div className="d-flex align-items-start gap-2">
                                            <span className="common-color commont-Font-Bold fs-5">✓</span>
                                            <div>
                                                <div className="common-FontWeight common-FontSize-small text-dark">Comprehensive Reports</div>
                                                <div className="text-muted common-Font-10">Filter revenue and patient data daily, monthly, or by range.</div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-sm-6">
                                        <div className="d-flex align-items-start gap-2">
                                            <span className="common-color commont-Font-Bold fs-5">✓</span>
                                            <div>
                                                <div className="common-FontWeight common-FontSize-small text-dark">Multi-User Access Control</div>
                                                <div className="text-muted common-Font-10">Role-based security for Admins, Techs, and Receptionists.</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Right Side: Login Form */}
                            <div className="col-lg-5 col-md-8 col-sm-10" id="login-form">
                                <div className="card login-card common-box-shadow common-box-border p-4 bg-white">
                                    <div className="text-center mb-4">
                                        <Logo
                                            width="64px" height="64px"
                                        />
                                        <h4 className="ventara-heading mt-2 mb-1">Sign In</h4>
                                        <p className="text-muted common-FontSize-small mb-0">Enter your credentials to access your lab portal</p>
                                    </div>

                                    <form onSubmit={FormSubmut}>
                                        <div className="mb-3 text-start">
                                            <label className="common-Labels mb-1" htmlFor="email">
                                                Email Address <span className="common-Asterisk">*</span>
                                            </label>
                                            <input
                                                type="email"
                                                className="form-control common-Input p-2"
                                                id="email"
                                                name="Email"
                                                placeholder="name@labdomain.com"
                                                required
                                                onChange={onchange}
                                            />
                                        </div>

                                        <div className="mb-3 text-start">
                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                <label className="common-Labels mb-0" htmlFor="password">
                                                    Password <span className="common-Asterisk">*</span>
                                                </label>
                                            </div>
                                            <input
                                                className="form-control common-Input p-2"
                                                type="password"
                                                id="password"
                                                name="Password"
                                                placeholder="••••••••"
                                                required
                                                onChange={onchange}
                                            />
                                        </div>

                                        <div className="mt-4">
                                            <button 
                                                type="submit" 
                                                className="Submit_Button w-100 common-FontFamily common-FontWeight common-cursor py-2"
                                            >
                                                Sign In
                                            </button>
                                        </div>
                                    </form>

                                    <div className="mt-4 pt-3 border-top text-center">
                                        <p className="text-muted common-Font-10 mb-0">
                                            Need technical support or account provisioning? <br />
                                            <span className="common-Link common-FontWeight">Contact System Administrator</span>
                                        </p>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </main>

                {/* Simple Footer */}
                <footer className="py-2 px-4 bg-white border-top text-center text-muted common-Font-10">
                    &copy; {new Date().getFullYear()} Care Linq EMR & Diagnostics. All Rights Reserved.
                </footer>
            </div>
        </>
    );
};

export default Login;